// src/components/rules/CommunityRules.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';
import RuleCard from './RuleCard';
import RulesManagement from './RulesManagement';
import RulesAcknowledgment from './RulesAcknowledgment';
import '../../pages/UserDashboard.css';
import './CommunityRules.css';

function CommunityRules({ userData, onBack }) {
  const [rules, setRules] = useState([]);
  const [filteredRules, setFilteredRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showManagement, setShowManagement] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [showAcknowledgment, setShowAcknowledgment] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';

  const categories = [
    { value: 'all', label: 'All Rules', icon: '📋' },
    { value: 'general', label: 'General', icon: '📌' },
    { value: 'noise', label: 'Noise', icon: '🔇' },
    { value: 'parking', label: 'Parking', icon: '🚗' },
    { value: 'pets', label: 'Pets', icon: '🐕' },
    { value: 'safety', label: 'Safety', icon: '🔒' },
    { value: 'common_areas', label: 'Common Areas', icon: '🏢' }
  ];

  useEffect(() => {
    fetchRules();
    checkAcknowledgment();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [darkMode]);

  useEffect(() => {
    filterRules();
  }, [rules, selectedCategory, searchTerm]);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('community_rules')
        .select('*')
        .eq('is_active', true)
        .order('category')
        .order('rule_number');

      if (error) throw error;
      setRules(data || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAcknowledgment = async () => {
    if (!userData?.id) return;
    
    try {
      const { data } = await supabase
        .from('rule_acknowledgments')
        .select('*')
        .eq('resident_id', userData.id)
        .single();
      
      setHasAcknowledged(!!data);
      if (!data) {
        setShowAcknowledgment(true);
      }
    } catch (error) {
      console.log('No acknowledgment found');
    }
  };

  const filterRules = () => {
    let filtered = [...rules];
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(rule => rule.category === selectedCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(rule => 
        rule.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredRules(filtered);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    let content = 'COMMUNITY RULES AND REGULATIONS\n';
    content += '================================\n\n';
    
    categories.forEach(cat => {
      if (cat.value === 'all') return;
      const catRules = rules.filter(r => r.category === cat.value);
      if (catRules.length > 0) {
        content += `\n${cat.label}\n${'-'.repeat(cat.label.length)}\n`;
        catRules.forEach(rule => {
          content += `\n${rule.rule_number}. ${rule.title}\n`;
          content += `${rule.description}\n`;
          if (rule.fine_amount) {
            content += `Fine: ₹${rule.fine_amount}\n`;
          }
        });
      }
    });
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'community_rules.txt';
    a.click();
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  if (showManagement && isAdmin) {
    return <RulesManagement userData={userData} onBack={() => {
      setShowManagement(false);
      fetchRules();
    }} />;
  }

  const stats = {
    total: rules.length,
    categories: [...new Set(rules.map(r => r.category))].length,
    critical: rules.filter(r => r.severity === 'critical').length,
    withFines: rules.filter(r => r.fine_amount > 0).length
  };

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className="menu-item active"
            onClick={() => closeMobileMenu()}
          >
            <span className="menu-icon">📋</span>
            <span className="menu-label">View Rules</span>
          </button>
          
          {isAdmin && (
            <button
              className="menu-item"
              onClick={() => {
                setShowManagement(true);
                closeMobileMenu();
              }}
            >
              <span className="menu-icon">⚙️</span>
              <span className="menu-label">Manage Rules</span>
            </button>
          )}
          
          <div className="sidebar-divider"></div>
          
          <div className="filter-section">
            <label className="filter-label">Filter by Category</label>
            <div className="category-filters">
              {categories.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`category-btn ${selectedCategory === cat.value ? 'active' : ''}`}
                >
                  {cat.icon} {cat.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="sidebar-divider"></div>
          
          {!hasAcknowledged && (
            <button
              className="menu-item highlight"
              onClick={() => setShowAcknowledgment(true)}
            >
              <span className="menu-icon">✅</span>
              <span className="menu-label">Acknowledge Rules</span>
            </button>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">📋 Community Rules</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="rules-header">
            <h2 className="page-title">📋 Community Rules & Regulations</h2>
            <div className="header-actions">
              <button onClick={handlePrint} className="btn-secondary">
                🖨️ Print
              </button>
              <button onClick={handleDownload} className="btn-secondary">
                📥 Download
              </button>
              {isAdmin && (
                <button onClick={() => setShowManagement(true)} className="btn-primary">
                  ⚙️ Manage
                </button>
              )}
            </div>
          </div>

          {/* Acknowledgment Alert */}
          {!hasAcknowledged && (
            <div className="alert alert-warning">
              <strong>⚠️ Important:</strong> Please read and acknowledge the community rules.
              <button
                onClick={() => setShowAcknowledgment(true)}
                className="btn-warning-inline"
              >
                Acknowledge Rules
              </button>
            </div>
          )}

          {/* Stats Cards */}
          <div className="dashboard-grid stats-grid">
            <div className="dashboard-card stat-card gradient-purple">
              <div className="stat-content">
                <h3>📊 Total</h3>
                <p className="stat-value">{stats.total}</p>
                <small>Rules</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-blue">
              <div className="stat-content">
                <h3>📁 Categories</h3>
                <p className="stat-value">{stats.categories}</p>
                <small>Types</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-red">
              <div className="stat-content">
                <h3>⚠️ Critical</h3>
                <p className="stat-value">{stats.critical}</p>
                <small>High Priority</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-yellow">
              <div className="stat-content">
                <h3>💰 With Fines</h3>
                <p className="stat-value">{stats.withFines}</p>
                <small>Penalty Rules</small>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="dashboard-card search-card">
            <input
              type="text"
              placeholder="🔍 Search rules..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Category Tabs */}
          <div className="rules-tabs">
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`tab-button ${selectedCategory === cat.value ? 'active' : ''}`}
              >
                {cat.icon} <span className="tab-label">{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Rules Display */}
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading rules...</p>
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="dashboard-card empty-state">
              <div className="empty-icon">📭</div>
              <p>No rules found</p>
              <small>Try adjusting your filters</small>
            </div>
          ) : (
            <div className="rules-list">
              {filteredRules.map(rule => (
                <RuleCard key={rule.id} rule={rule} />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Acknowledgment Modal */}
      {showAcknowledgment && (
        <RulesAcknowledgment
          rules={rules}
          userData={userData}
          onAcknowledge={() => {
            setHasAcknowledged(true);
            setShowAcknowledgment(false);
          }}
          onClose={() => setShowAcknowledgment(false)}
        />
      )}
    </div>
  );
}

export default CommunityRules;